/**
 * Shared correct / incorrect / unsure controls + inline comment for an
 * answer. Used in three places — the Q&A panel `AnswersRow`, report cells'
 * bullet-list multi-answer branch, and the graph-full "View Answer" branch.
 *
 * Treats the most recent feedback row from the *current user* as the
 * authoritative state of the controls; other users' rows show up in lists
 * elsewhere (e.g. an evidence/discussion view), not in this control.
 */

import { memo, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, X, HelpCircle, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import {
  type AnswerFeedback,
  type FeedbackCategory,
  type FeedbackStatus,
  FEEDBACK_CATEGORY_LABELS,
  categoriesFor,
  markFeedback,
  useAnswerFeedback,
} from "@/api/answer-feedback";

interface Props {
  questionId: string;
  answerGroupId: string;
  /** Optional answer (DocumentAnswer) id — narrows the feedback row to a
   *  specific extraction within a group, useful when one AnswerGroup carries
   *  multiple DocumentAnswers (composites, time-series). */
  answerId?: string | null;
  /** Set when the control is rendered inside a report cell so feedback
   *  carries traceability back to the originating report. */
  reportContextId?: string | null;
  /** Layout variant. `compact` collapses controls behind a small icon row;
   *  `inline` shows pills + a comment-toggle button on one line. */
  variant?: "inline" | "compact";
  /** Optional extra class applied to the outer container. */
  className?: string;
}

const STATUS_OPTIONS: Array<{
  value: FeedbackStatus;
  label: string;
  Icon: typeof Check;
  activeClasses: string;
  idleClasses: string;
}> = [
  {
    value: "correct",
    label: "Correct",
    Icon: Check,
    activeClasses: "bg-emerald-100 text-emerald-800 border-emerald-200",
    idleClasses: "text-gray-500 hover:bg-emerald-50 hover:text-emerald-700",
  },
  {
    value: "incorrect",
    label: "Incorrect",
    Icon: X,
    activeClasses: "bg-rose-100 text-rose-800 border-rose-200",
    idleClasses: "text-gray-500 hover:bg-rose-50 hover:text-rose-700",
  },
  {
    value: "unsure",
    label: "Unsure",
    Icon: HelpCircle,
    activeClasses: "bg-amber-100 text-amber-800 border-amber-200",
    idleClasses: "text-gray-500 hover:bg-amber-50 hover:text-amber-700",
  },
];

/** Pick the most recent value-target row authored by the current user, if
 *  any. Server returns a flat list ordered by created_at desc.
 *
 *  Defensive: skips falsy entries so a stale react-query cache or a
 *  bad splice can't crash the render. Without this, one undefined row
 *  in the cache (which can sneak in from a setQueriesData call before
 *  the new BE endpoint shape was loaded) propagates as a hard render
 *  error and the whole component tree boundary-fails. */
function pickOwnLatest(
  rows: AnswerFeedback[] | undefined,
  currentUserEmail: string | null | undefined,
): AnswerFeedback | undefined {
  if (!rows || !currentUserEmail) return undefined;
  return rows.find(
    (r) =>
      r &&
      r.feedback_target === "value" &&
      r.user_email === currentUserEmail,
  );
}

function AnswerFeedbackControlsImpl({
  questionId,
  answerGroupId,
  answerId,
  reportContextId,
  variant = "inline",
  className = "",
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentUserEmail = useCurrentUserEmail();

  const { data: feedback } = useAnswerFeedback({
    questionId,
    answerGroupId,
    target: "value",
  });
  const own = pickOwnLatest(feedback, currentUserEmail);

  const [pendingStatus, setPendingStatus] = useState<FeedbackStatus | null>(
    null,
  );
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [category, setCategory] = useState<FeedbackCategory | "">("");
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state from server only when the popover is closed —
  // while the user is editing, server refetches (triggered by post-
  // submit invalidation OR by sibling components polling) must not
  // overwrite their in-progress comment / category. Re-renders during
  // an open popover also destabilise the Save button (Radix repositions
  // on parent re-render). Reduce deps to `own.id + popoverOpen` so the
  // sync happens once per row, not on every server-side change.
  useEffect(() => {
    if (own && !popoverOpen) {
      setComment(own.comment);
      setCategory(own.category ?? "");
    }
  }, [own?.id, popoverOpen]);

  // Once the refetched server row catches up to a pending status, drop the
  // pending marker. Until then `activeStatus` keeps reading `pendingStatus`
  // so the controls render consistently across the brief POST→refetch race
  // — and crucially so a follow-up Save click doesn't bail with "pick a
  // status first" while the refetch is still in flight.
  useEffect(() => {
    if (pendingStatus && own?.status === pendingStatus) {
      setPendingStatus(null);
    }
  }, [pendingStatus, own?.status]);

  const activeStatus: FeedbackStatus | null =
    pendingStatus ?? own?.status ?? null;

  const submit = async (
    status: FeedbackStatus,
    commentText: string,
    categoryValue: FeedbackCategory | "",
  ) => {
    setIsSaving(true);
    try {
      const newRow = await markFeedback(questionId, {
        feedback_target: "value",
        answer_group: answerGroupId,
        answer: answerId ?? undefined,
        status,
        comment: commentText,
        category: categoryValue || null,
        report_context: reportContextId ?? undefined,
      });
      // Direct cache update across every view that surfaces this
      // AnswerGroup. The Q&A panel uses `questionId=question.question_id`
      // while a report cell uses `questionId=source_question_id` — they
      // can differ for the same AnswerGroup, so namespace-wide
      // invalidation would refetch every row's query (causing Radix
      // popover instability under polling) AND surgical invalidation
      // would miss the alternate view (causing stale "no feedback"
      // pills in the other tab — and a user could overwrite their
      // own feedback because the UI lied about persisted state).
      //
      // setQueriesData with a predicate matches every cached
      // useAnswerFeedback that filters on this answerGroupId, splices
      // the new row in, and skips the network entirely. Cross-view
      // shows the latest state immediately; no extra refetches; no
      // popover-instability tradeoff. This replaces the prior
      // surgical-invalidate path.
      const matchesThisGroup = (key: readonly unknown[]): boolean => {
        // Key shape per `answerFeedbackKeys.forQuestionFiltered`:
        // ["answer-feedback", "question", questionId, answerGroup, target]
        if (key[0] !== "answer-feedback" || key[1] !== "question") return false;
        const keyAnswerGroup = key[3];
        const keyTarget = key[4];
        if (keyAnswerGroup !== answerGroupId) return false;
        // Match queries with our target ("value") or no target filter.
        return keyTarget === null || keyTarget === "value";
      };
      // Guard against an undefined/null POST result corrupting the
      // cache — only splice if we got a real row back. apiClient
      // throws on non-2xx, so reaching this code path means a 200/201
      // body, but defense-in-depth here keeps a future BE shape change
      // from poisoning every query that matches the predicate.
      if (newRow && newRow.id) {
        queryClient.setQueriesData<AnswerFeedback[]>(
          { predicate: (q) => matchesThisGroup(q.queryKey) },
          (old) => {
            // Filter out any stale `undefined` entries the cache may
            // already carry from earlier bugs / cross-version state.
            const list = (old ?? []).filter((r): r is AnswerFeedback => Boolean(r));
            const idx = list.findIndex((r) => r.id === newRow.id);
            if (idx >= 0) {
              const next = [...list];
              next[idx] = newRow;
              return next;
            }
            // Server returns rows newest-first; prepend so pickOwnLatest
            // resolves to this row immediately.
            return [newRow, ...list];
          },
        );
      }
      // Don't clear pendingStatus here — the useEffect above does it once
      // `own.status` matches, which is the only guarantee that
      // `activeStatus` stays well-defined for the next interaction.
    } catch (e) {
      // Roll back the optimistic state — otherwise the failed status
      // keeps rendering as active even though it never persisted.
      setPendingStatus(null);
      toast({
        title: "Couldn't save feedback",
        description:
          e instanceof Error ? e.message : "Try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onPick = (status: FeedbackStatus) => {
    // Re-clicking the same active status is a no-op (popover stays
    // closed; nothing changes).
    if (own && own.status === status) {
      return;
    }
    // Sanitize the category against the new status — the negative and
    // affirmative shapes are disjoint, so e.g. flipping from Incorrect
    // (with category=extraction_wrong_value) to Correct must drop the
    // stale negative category.
    const validForNext = categoriesFor(status);
    const sanitizedCategory: FeedbackCategory | "" =
      category && validForNext.includes(category as FeedbackCategory)
        ? category
        : "";
    if (sanitizedCategory !== category) setCategory(sanitizedCategory);

    // Optimistic UI only — the pill flips to the chosen color via
    // pendingStatus. NO POST yet. The user types the rationale (or
    // not) and clicks Save in the popover; that's the single POST
    // for this logical action. If they dismiss the popover without
    // saving, pendingStatus is cleared (see Popover onOpenChange) and
    // no row is created.
    //
    // The earlier "auto-save on pill click" behaviour produced two
    // rows per logical action (verdict-only on click, then
    // verdict+comment on save), which inflated row counts in the admin
    // and complicated Phase 3 clustering. One POST per logical user
    // action keeps the data clean. The trade-off — a user who clicks
    // a pill and walks away without typing leaves no record — is
    // acceptable: that signal was always weak (could be exploration,
    // mis-click, distraction).
    setPendingStatus(status);
    setPopoverOpen(true);
  };

  const onSaveComment = async () => {
    const status = activeStatus;
    if (!status) {
      toast({
        title: "Pick correct, incorrect, or unsure first",
        description: "A comment alone isn't enough to save feedback.",
      });
      return;
    }
    await submit(status, comment, category);
    setPopoverOpen(false);
  };

  // Outer ring color reflects the persisted feedback status so the
  // user can scan reviewed rows at a glance. Soft tints to avoid
  // competing with existing cell backgrounds. Padding is unconditional
  // (rounded-md + px-1 py-0.5 always applied) so the container's
  // height doesn't change when activeStatus flips null → set on the
  // first click. Without this, the Q&A row reflows mid-interaction,
  // drifts the popover trigger, and Radix re-anchors the popover
  // content — Save button becomes "not stable" mid-click.
  const ringClass =
    activeStatus === "correct"
      ? "ring-1 ring-emerald-200/70"
      : activeStatus === "incorrect"
        ? "ring-1 ring-rose-200/70"
        : activeStatus === "unsure"
          ? "ring-1 ring-amber-200/70"
          : "";

  return (
    <div
      className={`flex items-center gap-1 rounded-md px-1 py-0.5 ${ringClass} ${className}`}
    >
      <TooltipProvider delayDuration={300}>
        {STATUS_OPTIONS.map(({ value, label, Icon, activeClasses, idleClasses }) => {
          const isActive = activeStatus === value;
          return (
            <Tooltip key={value}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={label}
                  aria-pressed={isActive}
                  disabled={isSaving}
                  onClick={() => onPick(value)}
                  className={
                    `inline-flex items-center justify-center rounded border transition-colors ` +
                    (variant === "compact"
                      ? `h-5 w-5 text-[11px] `
                      : `h-6 px-1.5 gap-1 text-[11px] `) +
                    (isActive
                      ? activeClasses
                      : `border-transparent ${idleClasses}`) +
                    (isSaving ? ` opacity-60 cursor-wait` : ``)
                  }
                >
                  <Icon size={12} />
                  {variant === "inline" && <span>{label}</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{label}</TooltipContent>
            </Tooltip>
          );
        })}

        {/* Comment button → opens the reasoning popover. Same data
            captured (category + textarea + save/cancel) but in a
            floating card instead of an inline expansion below the row,
            so dense report cells stay tidy when feedback is given. */}
        <Popover
          open={popoverOpen}
          onOpenChange={(next) => {
            setPopoverOpen(next);
            // Closing without saving rolls back the optimistic pill —
            // pendingStatus drove the active color while the popover
            // was open; clearing it on close lets activeStatus fall
            // back to the persisted own.status (or null if no prior
            // feedback). Save sets own.status via its POST, so the
            // sequencing means the active color stays correct in
            // both code paths: dismiss → revert; save → confirmed.
            if (!next) setPendingStatus(null);
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={comment ? "Edit comment" : "Add comment"}
                  className={
                    `inline-flex items-center justify-center rounded border transition-colors ` +
                    (variant === "compact"
                      ? `h-5 w-5 text-[11px] `
                      : `h-6 px-1.5 gap-1 text-[11px] `) +
                    (comment
                      ? `bg-blue-50 text-blue-700 border-blue-200`
                      : `border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-800`)
                  }
                >
                  <MessageSquare size={12} />
                  {variant === "inline" && (
                    <span>{comment ? "Edit comment" : "Comment"}</span>
                  )}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">
              {comment ? "Edit comment" : "Add comment"}
            </TooltipContent>
          </Tooltip>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="w-80 p-3 flex flex-col gap-2"
          >
            <div className="flex items-center justify-between">
              <h5 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {activeStatus === "correct"
                  ? "What's working?"
                  : activeStatus
                    ? "What's the reason?"
                    : "Add a reason"}
              </h5>
              <button
                type="button"
                onClick={() => setPopoverOpen(false)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            </div>

            {/* Typed reason — drives Phase 3 clustering. The option
                list swaps based on status (affirmative for `correct`,
                negative for incorrect/unsure). Optional. */}
            {activeStatus && (
              <select
                value={category}
                onChange={(e) =>
                  setCategory(e.target.value as FeedbackCategory | "")
                }
                className="text-xs rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
              >
                <option value="">
                  {activeStatus === "correct"
                    ? "What's working? (optional)"
                    : "What's the reason? (optional)"}
                </option>
                {categoriesFor(activeStatus).map((c) => (
                  <option key={c} value={c}>
                    {FEEDBACK_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            )}

            <Textarea
              aria-label="Feedback comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                // Cmd+Enter (mac) / Ctrl+Enter (win/linux) saves.
                // Plain Enter inserts a newline (multi-line ok).
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  if (!isSaving) onSaveComment();
                }
              }}
              placeholder={
                activeStatus === "incorrect"
                  ? "Why is this wrong? (e.g. doc says X, model said Y) — ⌘/Ctrl+Enter to save"
                  : activeStatus === "correct"
                    ? "Why is this right? (optional) — ⌘/Ctrl+Enter to save"
                    : "What's unclear about this answer? — ⌘/Ctrl+Enter to save"
              }
              className="min-h-[80px] text-xs"
            />

            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => {
                  setComment(own?.comment ?? "");
                  setCategory(own?.category ?? "");
                  setPopoverOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                // Reserve enough width to fit "Saving…" so the button
                // doesn't grow → snap back when isSaving toggles. Cancel
                // sits next to it; the width oscillation was a visible
                // ~20px shift mid-POST.
                className="h-7 text-xs min-w-[72px]"
                disabled={isSaving}
                onClick={onSaveComment}
              >
                {isSaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </TooltipProvider>
    </div>
  );
}

/** Memo-wrap so polling-driven parent re-renders (q-and-a.tsx
 *  invalidates ["answers"] every 5s while questions are pending; report
 *  cells refresh on related cache updates) don't ripple through to the
 *  popover. Without this, Radix re-anchors the popover content on every
 *  parent render, drifting the Save button and breaking click stability.
 *  Props are all primitives (UUIDs + variant string) so the default
 *  shallow compare is sufficient. */
export const AnswerFeedbackControls = memo(AnswerFeedbackControlsImpl);

/** Read the current user's email from React Query cache (populated by
 *  `useUser` on app boot). Avoids requiring callers to thread the email
 *  through every place this control is rendered. */
function useCurrentUserEmail(): string | null {
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<{ email?: string } | undefined>([
    "currentUser",
  ]);
  return data?.email ?? null;
}
